import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@components/atoms/Card';
import { useTranslation } from '@hooks/useTranslation';

export interface NotFoundProps {
  isAuthenticated: boolean;
}

/**
 * Sprint 39, story-212. The 404 card. Reachable before sign-in, so copy comes from the same
 * pre-auth locale source the login page uses. Never echoes the requested URL.
 */
export const NotFound: React.FC<NotFoundProps> = ({ isAuthenticated }) => {
  const tNotFound = useTranslation('notFound');

  useEffect(() => {
    const previous = document.title;
    document.title = tNotFound.title;
    return () => {
      document.title = previous;
    };
  }, [tNotFound.title]);

  return (
    <div
      className="flex justify-center items-center min-h-screen p-4"
      data-testid="not-found-page">
      <Card className="w-full max-w-md mx-auto text-center">
        <div className="flex flex-col items-center gap-4">
          <p className="text-5xl font-bold text-indigo-600 dark:text-indigo-400">
            {tNotFound.code}
          </p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {tNotFound.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">{tNotFound.body}</p>
          <Link
            to={isAuthenticated ? '/' : '/login'}
            data-testid="not-found-cta"
            className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800">
            {isAuthenticated ? tNotFound.goToHome : tNotFound.goToLogin}
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default NotFound;
